import React from 'react';

import { Box, Typography, type SxProps, type Theme } from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import { ArrowRightIcon, IvyIcon } from '@ivy/components/icons';
import { gql, type FragmentType, getFragmentData } from '@ivy/gql/types';
import { combineSx } from '@ivy/lib/styling/sx';
import { buildInternalLink } from '@ivy/lib/util/route';
import useWhitelabel from '@ivy/lib/whitelabel/useWhitelabel';

const OrgWhitelabelCta_OrgFDoc = gql(/* GraphQL */ `
	fragment OrgWhitelabelCta_Org on organization {
		id
		slug
	}
`);

export interface AcepCtaProps {
	org: FragmentType<typeof OrgWhitelabelCta_OrgFDoc>;
	sx?: SxProps<Theme>;
}

const OrgWhitelabelCta = ({ org: rawOrg, sx }: AcepCtaProps) => {
	const org = getFragmentData(OrgWhitelabelCta_OrgFDoc, rawOrg);
	const wl = useWhitelabel();

	return (
		<Box
			sx={combineSx(
				{
					borderRadius: (theme) =>
						`${theme.cardShape?.borderRadius || theme.shape.borderRadius}px`,
					bgcolor: 'primary.translucent',
					position: 'relative',
					overflow: 'hidden',
					p: {
						xs: 3,
						sm: 5,
					},
				},
				sx,
			)}
		>
			<Box
				component='img'
				src='https://assets.ivyclinicians.io/images/org-whitelabel-cta-background.png'
				sx={{
					height: '135%',
					position: 'absolute',
					bottom: 0,
					left: '60%',
					zIndex: 1,
				}}
			/>
			<Box width='60%'>
				{/* TODO: some way to apply to employer on employer page? Or instructions on how to */}
				<Typography variant='body1' color='text.icon' mb={2}>
					Interested in jobs, salaries, and reviews for this employer?
				</Typography>
				<Typography variant='body1'>
					{/* We use an 'a' tag so that bots pick up this backlink but use JavaScript to stop the event and show a redirect screen */}
					<RouteLink
						fontWeight='bold'
						to={{
							pathname: buildInternalLink(RouteLink.routes.ORG_SHOW, {
								orgId: [org.id, org.slug],
							}),
							whitelabel: wl.default.name,
						}}
						underline='hover'
					>
						<Box component='span' sx={{ verticalAlign: 'middle' }}>
							Go to{' '}
							<IvyIcon
								sx={{
									verticalAlign: 'bottom',
								}}
							/>{' '}
							{wl.default.productName}
						</Box>
						<ArrowRightIcon
							sx={{
								ml: 1,
								fontSize: 'inherit',
								color: 'inherit',
								verticalAlign: 'middle',
							}}
						/>
					</RouteLink>
				</Typography>
			</Box>
		</Box>
	);
};

export default OrgWhitelabelCta;
